import React, { useEffect, createElement } from 'react';
import { CheckCircle, FileText, Users, Package, CreditCard, Clock } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { FAQAccordion } from '../components/FAQAccordion';
import { CTASection } from '../components/CTASection';
import { Footer } from '../components/Footer';
interface ServicePageProps {
  serviceName: string;
  introText: string;
  whatItIs: {
    title: string;
    content: string;
  };
  whoItsFor: {
    title: string;
    content: string;
    targetAudience: string[];
  };
  whatYouGet: {
    title: string;
    content: string;
    deliverables: string[];
  };
  outcomes: string[];
  faqs: Array<{
    question: string;
    answer: string;
  }>;
  metaTitle: string;
  metaDescription: string;
}
export function ServicePage({
  serviceName,
  introText,
  whatItIs,
  whoItsFor,
  whatYouGet,
  outcomes,
  faqs,
  metaTitle,
  metaDescription
}: ServicePageProps) {
  useEffect(() => {
    document.title = metaTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', metaDescription);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = metaDescription;
      document.head.appendChild(meta);
    }
  }, [metaTitle, metaDescription]);
  return <div className="min-h-screen bg-warm-50" data-id="element-1047">
      <Navigation data-id="element-1048" />

      {/* Hero Section */}
      <section className="bg-cta-500 pt-32 pb-16 lg:pt-40 lg:pb-24" data-id="element-1049">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-1050">
          <img src="https://cdn.magicpatterns.com/uploads/cfHMrrPng3iagpRwV1FuNs/Favicon.jpg" alt="UK Medicolegal Assessments shield logo" className="h-12 w-12 rounded-lg object-contain mb-6" data-id="element-1051" />
          <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-white mb-6 leading-[1.1] tracking-tight" data-id="element-1052">
            {serviceName} Reports UK –{' '}
            <span className="text-teal-300" data-id="element-1053">UK Medicolegal Assessments</span>
          </h1>
          <p className="text-xl text-blue-100 leading-relaxed" data-id="element-1054">{introText}</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-1055">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-1056">
          {/* What It Is */}
          <div className="mb-16 md:mb-20" data-id="element-1057">
            <div className="flex flex-col md:flex-row items-start gap-6" data-id="element-1058">
              <div className="bg-teal-50 p-4 rounded-2xl flex-shrink-0" data-id="element-1059">
                <FileText className="h-8 w-8 text-teal-600" data-id="element-1060" />
              </div>
              <div data-id="element-1061">
                <h2 className="text-2xl md:text-3xl font-bold text-ink-900 mb-4 tracking-tight" data-id="element-1062">
                  {whatItIs.title}
                </h2>
                <p className="text-lg text-ink-500 leading-relaxed" data-id="element-1063">
                  {whatItIs.content}
                </p>
              </div>
            </div>
          </div>

          {/* Who It's For */}
          <div className="mb-16 md:mb-20" data-id="element-1064">
            <div className="flex flex-col md:flex-row items-start gap-6" data-id="element-1065">
              <div className="bg-teal-50 p-4 rounded-2xl flex-shrink-0" data-id="element-1066">
                <Users className="h-8 w-8 text-teal-600" data-id="element-1067" />
              </div>
              <div className="flex-1 w-full" data-id="element-1068">
                <h2 className="text-2xl md:text-3xl font-bold text-ink-900 mb-4 tracking-tight" data-id="element-1069">
                  {whoItsFor.title}
                </h2>
                <p className="text-lg text-ink-500 leading-relaxed mb-8" data-id="element-1070">
                  {whoItsFor.content}
                </p>
                <div className="bg-warm-50 rounded-2xl p-8 border border-warm-200" data-id="element-1071">
                  <h3 className="font-bold text-ink-900 mb-4 text-lg" data-id="element-1072">
                    Ideal for:
                  </h3>
                  <ul className="space-y-3" data-id="element-1073">
                    {whoItsFor.targetAudience.map((audience, index) => <li key={index} className="flex items-start text-ink-700 font-medium" data-id="element-1074">
                        <CheckCircle className="h-5 w-5 text-sage-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1075" />
                        <span data-id="element-1076">{audience}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* What You Get */}
          <div className="mb-16 md:mb-20" data-id="element-1077">
            <div className="flex flex-col md:flex-row items-start gap-6" data-id="element-1078">
              <div className="bg-teal-50 p-4 rounded-2xl flex-shrink-0" data-id="element-1079">
                <Package className="h-8 w-8 text-teal-600" data-id="element-1080" />
              </div>
              <div className="flex-1 w-full" data-id="element-1081">
                <h2 className="text-2xl md:text-3xl font-bold text-ink-900 mb-4 tracking-tight" data-id="element-1082">
                  {whatYouGet.title}
                </h2>
                <p className="text-lg text-ink-500 leading-relaxed mb-8" data-id="element-1083">
                  {whatYouGet.content}
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-1084">
                  {whatYouGet.deliverables.map((deliverable, index) => <div key={index} className="flex items-start bg-white border border-warm-200 rounded-xl p-5 shadow-sm" data-id="element-1085">
                      <CheckCircle className="h-5 w-5 text-sage-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1086" />
                      <span className="text-ink-700 font-medium" data-id="element-1087">
                        {deliverable}
                      </span>
                    </div>)}
                </div>

                <div className="bg-teal-50 border border-teal-100 rounded-2xl p-6 md:p-8" data-id="element-1088">
                  <div className="flex items-start" data-id="element-1089">
                    <CreditCard className="h-6 w-6 text-teal-600 mr-4 flex-shrink-0 mt-1" data-id="element-1090" />
                    <div data-id="element-1091">
                      <h3 className="font-bold text-teal-800 mb-2 text-lg" data-id="element-1092">
                        Competitive Pricing
                      </h3>
                      <p className="text-teal-700/90 leading-relaxed" data-id="element-1093">
                        We provide transparent, competitive fee quotes tailored
                        to your case requirements. Contact us for a detailed
                        quotation with no obligation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Outcomes */}
          <div className="mb-16 md:mb-24 bg-cta-500 rounded-[2rem] p-8 md:p-12 shadow-lg" data-id="element-1094">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 tracking-tight" data-id="element-1095">
              Expected Outcomes
            </h2>
            <div className="grid md:grid-cols-2 gap-5" data-id="element-1096">
              {outcomes.map((outcome, index) => <div key={index} className="flex items-start text-blue-100" data-id="element-1097">
                  <CheckCircle className="h-5 w-5 text-teal-300 mr-3 flex-shrink-0 mt-0.5" data-id="element-1098" />
                  <span className="leading-relaxed" data-id="element-1099">{outcome}</span>
                </div>)}
              <div className="flex items-start text-blue-100" data-id="element-1100">
                <Clock className="h-5 w-5 text-teal-300 mr-3 flex-shrink-0 mt-0.5" data-id="element-1101" />
                <span className="leading-relaxed" data-id="element-1102">
                  Rapid response to instructions (within 24 hours; mark "Urgent"
                  for priority)
                </span>
              </div>
              <div className="flex items-start text-blue-100" data-id="element-1103">
                <CreditCard className="h-5 w-5 text-teal-300 mr-3 flex-shrink-0 mt-0.5" data-id="element-1104" />
                <span className="leading-relaxed" data-id="element-1105">
                  Transparent fee quote provided upfront
                </span>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div data-id="element-1106">
            <h2 className="text-3xl md:text-4xl font-bold text-ink-900 mb-10 text-center tracking-tight" data-id="element-1107">
              Frequently Asked Questions
            </h2>
            <FAQAccordion items={faqs} data-id="element-1108" />
          </div>
        </div>
      </section>

      <CTASection data-id="element-1109" />
      <Footer data-id="element-1110" />
    </div>;
}